import dayjs from "dayjs";
import { KYDEventBlockData, KYDEventBlockDataV2 } from "interfaces/KYDCampaign";
import { KYDGender } from "interfaces/KYDUser";
import {
  CampaignType,
  OptimizationGoal,
  RatioType,
  PublisherPlatforms,
  RatioPositions,
  FacebookPositions,
  InstagramPositions,
} from "../../interfaces/KYDMarketing";

export const genderToText = (gender: KYDGender | "any") => {
  switch (gender) {
    case "male":
      return "Male";
    case "female":
      return "Female";
    case "any":
      return "Any";
    case "prefernottosay":
      return "-";
  }
};

export const replaceCampaignVariables = (
  text: string,
  eventData: KYDEventBlockData | KYDEventBlockDataV2
) => {
  // Safety check for required data
  if (!text || !eventData?.event) {
    return text || "";
  }
  const availableVariables = [
    {
      dynamicKey: "{{start_at}}",
      objectKey: "start_at",
      format: (value: string) => dayjs(value).format("MMMM D, YYYY"),
    },
    {
      dynamicKey: "{{event_name}}",
      objectKey: "name",
    },
    {
      dynamicKey: "{{end_at}}",
      objectKey: "end_at",
      format: (value: string) => dayjs(value).format("MMMM D, YYYY"),
    },
    {
      dynamicKey: "{{link}}",
      objectKey: "link",
    },
    {
      dynamicKey: "{{venue}}",
      objectKey: "venue",
    },
  ];

  // Helper function to get variable value
  const getVariableValue = (variableKey: string) => {
    // Try to find variable with exact match first
    let variable = availableVariables.find((v) => v.dynamicKey === variableKey);

    // If not found, try with normalized underscores (for escaped variables)
    if (!variable) {
      const normalizedKey = variableKey.replace(/\\_/g, "_");
      variable = availableVariables.find((v) => v.dynamicKey === normalizedKey);
    }

    if (!variable) return null;

    // Get the value from the event object
    let value: any;
    if (variable.objectKey.includes(".")) {
      value = variable.objectKey
        .split(".")
        .reduce((obj, key) => obj?.[key], eventData.event);
    } else {
      value =
        eventData.event[variable.objectKey as keyof typeof eventData.event];
    }

    // Apply formatting if available
    if (variable.format && value) {
      return variable.format(value as string);
    }

    return value || "";
  };

  if (typeof text !== "string") {
    return text;
  }

  return text?.replace(/{{([^}]+)}}/g, (match: string, varName: string) => {
    // varName is the content between {{ and }}, so we need to reconstruct the full key
    const fullKey = `{{${varName}}}`;
    const value = getVariableValue(fullKey);
    return value || match;
  });
};

export const getDefaultOptimizationGoalForCampaignType = (
  campaign_type: CampaignType
): OptimizationGoal => {
  const default_goal_for_optimization_type: Record<
    CampaignType,
    OptimizationGoal
  > = {
    [CampaignType.AWARENESS]: OptimizationGoal.LANDING_PAGE_VIEWS,
    [CampaignType.CONVERSION]: OptimizationGoal.VALUE,
  };

  return default_goal_for_optimization_type[campaign_type];
};

export const resolveOptimizationGoalForCampaignType = ({
  campaign_type,
  configured_optimization_goal,
}: {
  campaign_type: CampaignType;
  configured_optimization_goal?: string | null;
}): OptimizationGoal => {
  const defaultGoal = getDefaultOptimizationGoalForCampaignType(campaign_type);
  if (!configured_optimization_goal) return defaultGoal;

  const supportedGoals = new Set(Object.values(OptimizationGoal));
  if (supportedGoals.has(configured_optimization_goal as OptimizationGoal)) {
    return configured_optimization_goal as OptimizationGoal;
  }

  return defaultGoal;
};

/**
 * Gets default positions for Facebook and Instagram based on media ratios
 * @param ratios Array of media ratios (portrait, square, etc.)
 * @returns Object containing default positions for each platform
 */
export const getDefaultPositionsFromRatios = (ratios: RatioType[]) => {
  const facebookPositions: FacebookPositions[] = [];
  const instagramPositions: InstagramPositions[] = [];

  // Get unique ratios
  const uniqueRatios = [...new Set(ratios)];

  uniqueRatios.forEach((ratio) => {
    if (RatioPositions[ratio]) {
      if (RatioPositions[ratio][PublisherPlatforms.facebook]) {
        facebookPositions.push(
          ...RatioPositions[ratio][PublisherPlatforms.facebook]
        );
      }
      if (RatioPositions[ratio][PublisherPlatforms.instagram]) {
        instagramPositions.push(
          ...RatioPositions[ratio][PublisherPlatforms.instagram]
        );
      }
    }
  });

  return {
    facebook: [...new Set(facebookPositions)], // Remove duplicates
    instagram: [...new Set(instagramPositions)], // Remove duplicates
  };
};

const unique = <T>(items: T[]) => [...new Set(items)];

/**
 * Meta rejects `feed` in `instagram_positions` (ad set targeting and asset customization).
 * Instagram feed is `stream`. Maps legacy stored `feed` to `stream` and dedupes.
 */
export function normalizeInstagramPositionsForMetaApi(
  positions: InstagramPositions[]
): InstagramPositions[] {
  return unique(
    positions.map((p) =>
      p === InstagramPositions.feed ? InstagramPositions.stream : p
    )
  );
}

const filterPositionsByAllowed = <T>(positions: T[], allowed: T[]) => {
  const allowedSet = new Set(allowed);
  return unique(positions.filter((position) => allowedSet.has(position)));
};

export type GetAssetFeedPlacementConfigInput = {
  facebookPositionsProvided: boolean;
  instagramPositionsProvided: boolean;
  facebookPositions: FacebookPositions[];
  instagramPositions: InstagramPositions[];
  availableRatios?: RatioType[];
};

export const getAssetFeedPlacementConfig = ({
  facebookPositionsProvided,
  instagramPositionsProvided,
  facebookPositions,
  instagramPositions,
  availableRatios,
}: GetAssetFeedPlacementConfigInput) => {
  const resolvedRatios =
    availableRatios && availableRatios.length > 0
      ? unique(availableRatios)
      : [RatioType.square, RatioType.portrait];
  const hasPortraitRatio = resolvedRatios.includes(RatioType.portrait);
  const hasSquareRatio = resolvedRatios.includes(RatioType.square);

  const defaultFacebookPortraitPositions = hasPortraitRatio
    ? RatioPositions[RatioType.portrait][PublisherPlatforms.facebook]
    : [];
  const defaultInstagramPortraitPositions = hasPortraitRatio
    ? RatioPositions[RatioType.portrait][PublisherPlatforms.instagram]
    : [];
  const defaultFacebookSquarePositions = hasSquareRatio
    ? RatioPositions[RatioType.square][PublisherPlatforms.facebook]
    : [];
  const defaultInstagramSquarePositions = hasSquareRatio
    ? RatioPositions[RatioType.square][PublisherPlatforms.instagram]
    : [];

  const facebookPositionsForPortrait =
    facebookPositionsProvided && facebookPositions.length > 0
      ? filterPositionsByAllowed(
          facebookPositions,
          defaultFacebookPortraitPositions
        )
      : [];
  const facebookPositionsForSquare =
    facebookPositionsProvided && facebookPositions.length > 0
      ? filterPositionsByAllowed(
          facebookPositions,
          defaultFacebookSquarePositions
        )
      : [];
  const instagramPositionsForPortrait =
    instagramPositionsProvided && instagramPositions.length > 0
      ? filterPositionsByAllowed(
          instagramPositions,
          defaultInstagramPortraitPositions
        )
      : [];
  const instagramPositionsForSquare =
    instagramPositionsProvided && instagramPositions.length > 0
      ? filterPositionsByAllowed(
          instagramPositions,
          defaultInstagramSquarePositions
        )
      : [];

  const resolvedPublisherPlatforms: PublisherPlatforms[] = [];
  const hasValidFacebookPositions =
    facebookPositionsForPortrait.length > 0 ||
    facebookPositionsForSquare.length > 0;
  const hasValidInstagramPositions =
    instagramPositionsForPortrait.length > 0 ||
    instagramPositionsForSquare.length > 0;

  if (facebookPositionsProvided ? hasValidFacebookPositions : true) {
    resolvedPublisherPlatforms.push(PublisherPlatforms.facebook);
  }
  if (instagramPositionsProvided ? hasValidInstagramPositions : true) {
    resolvedPublisherPlatforms.push(PublisherPlatforms.instagram);
  }
  if (
    resolvedPublisherPlatforms.length === 0 &&
    !facebookPositionsProvided &&
    !instagramPositionsProvided
  ) {
    resolvedPublisherPlatforms.push(
      PublisherPlatforms.facebook,
      PublisherPlatforms.instagram
    );
  }

  // No auto-append of stream/feed with Explore: honor explicit placement picks. KYD defaults
  // (RatioPositions square) still include stream alongside Explore when positions are not provided.
  const exploreInstagramRaw = instagramPositionsProvided
    ? instagramPositionsForSquare
    : defaultInstagramSquarePositions;
  const singleAssetInstagramRaw = instagramPositionsProvided
    ? unique([
        ...instagramPositionsForPortrait,
        ...instagramPositionsForSquare,
      ])
    : unique([
        ...defaultInstagramSquarePositions,
        ...defaultInstagramPortraitPositions,
      ]);

  const normIg = (list: InstagramPositions[]) =>
    normalizeInstagramPositionsForMetaApi(unique(list));

  return {
    publisherPlatforms: resolvedPublisherPlatforms,
    reels: {
      facebook: facebookPositionsProvided
        ? facebookPositionsForPortrait
        : defaultFacebookPortraitPositions,
      instagram: normIg(
        instagramPositionsProvided
          ? instagramPositionsForPortrait
          : defaultInstagramPortraitPositions
      ),
    },
    explore: {
      facebook: facebookPositionsProvided
        ? facebookPositionsForSquare
        : defaultFacebookSquarePositions,
      instagram: normIg(exploreInstagramRaw),
    },
    singleAsset: {
      facebook: facebookPositionsProvided
        ? unique([
            ...facebookPositionsForPortrait,
            ...facebookPositionsForSquare,
          ])
        : unique([
            ...defaultFacebookSquarePositions,
            ...defaultFacebookPortraitPositions,
          ]),
      instagram: normIg(singleAssetInstagramRaw),
    },
  };
};
